import React from 'react';

import type { Control, FieldValues, Path } from 'react-hook-form';
import { useController } from 'react-hook-form';

import { Box, MenuItem, TextField } from '@mui/material';

import {
  CONTENT_TAG_SELECTABLE_STATUSES,
  CONTENT_TAG_STATUS,
  CONTENT_TAG_STATUS_LABELS,
  type ContentTagStatus,
} from '../hooks';

export type ComponentTagFormState = {
  icon: string;
  title: string;
  subtitle: string;
  linkText: string;
  status: ContentTagStatus;
};

export const DEFAULT_COMPONENT_TAG_FORM_STATE: ComponentTagFormState = {
  icon: 'ticket',
  title: '',
  subtitle: '',
  linkText: '',
  status: CONTENT_TAG_STATUS.DRAFT,
};

export type TagContentFormData = {
  offerBanner: ComponentTagFormState;
  searchBanner: ComponentTagFormState;
  pill: ComponentTagFormState;
};

export const DEFAULT_TAG_CONTENT_FORM_DATA: TagContentFormData = {
  offerBanner: { ...DEFAULT_COMPONENT_TAG_FORM_STATE },
  searchBanner: { ...DEFAULT_COMPONENT_TAG_FORM_STATE },
  pill: { ...DEFAULT_COMPONENT_TAG_FORM_STATE },
};

type Props<TFormValues extends FieldValues> = {
  control: Control<TFormValues>;
  namePrefix: Path<TFormValues>;
  disableStatus?: boolean;
  showLinkText?: boolean;
};

export default function TagContentForm<TFormValues extends FieldValues>({
  control,
  namePrefix,
  disableStatus = false,
  showLinkText = false,
}: Props<TFormValues>) {
  const { field: titleField } = useController({
    control,
    name: `${namePrefix}.title` as Path<TFormValues>,
  });
  const { field: subtitleField } = useController({
    control,
    name: `${namePrefix}.subtitle` as Path<TFormValues>,
  });
  const { field: iconField } = useController({
    control,
    name: `${namePrefix}.icon` as Path<TFormValues>,
  });
  const { field: linkTextField } = useController({
    control,
    name: `${namePrefix}.linkText` as Path<TFormValues>,
  });
  const { field: statusField } = useController({
    control,
    name: `${namePrefix}.status` as Path<TFormValues>,
  });

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 2,
      }}
    >
      <Box>
        <TextField
          label="Title"
          value={titleField.value || ''}
          onChange={titleField.onChange}
          onBlur={titleField.onBlur}
          inputRef={titleField.ref}
          required
          fullWidth
        />
      </Box>
      <Box>
        <TextField
          label="Subtitle"
          value={subtitleField.value || ''}
          onChange={subtitleField.onChange}
          onBlur={subtitleField.onBlur}
          inputRef={subtitleField.ref}
          fullWidth
        />
      </Box>
      <Box>
        <TextField
          select
          label="Icon"
          value={iconField.value || ''}
          onChange={iconField.onChange}
          onBlur={iconField.onBlur}
          inputRef={iconField.ref}
          required
          fullWidth
        >
          <MenuItem value="ticket">ticket</MenuItem>
        </TextField>
      </Box>
      <Box>
        <TextField
          select
          label="Status"
          value={statusField.value ?? CONTENT_TAG_STATUS.DRAFT}
          onChange={statusField.onChange}
          onBlur={statusField.onBlur}
          inputRef={statusField.ref}
          required
          fullWidth
          disabled={disableStatus}
        >
          {CONTENT_TAG_SELECTABLE_STATUSES.map((value) => (
            <MenuItem key={value} value={value}>
              {CONTENT_TAG_STATUS_LABELS[value]}
            </MenuItem>
          ))}
          <MenuItem value={CONTENT_TAG_STATUS.CHANGED} disabled>
            {CONTENT_TAG_STATUS_LABELS[CONTENT_TAG_STATUS.CHANGED]}
          </MenuItem>
          <MenuItem value={CONTENT_TAG_STATUS.ARCHIVED} disabled>
            {CONTENT_TAG_STATUS_LABELS[CONTENT_TAG_STATUS.ARCHIVED]}
          </MenuItem>
        </TextField>
      </Box>
      {showLinkText && (
        <Box>
          <TextField
            label="Link Text"
            value={linkTextField.value || ''}
            onChange={linkTextField.onChange}
            onBlur={linkTextField.onBlur}
            inputRef={linkTextField.ref}
            helperText="This is the CTA URL, can be overridden on the website for dynamic CTAs (eg Disney)"
            fullWidth
          />
        </Box>
      )}
    </Box>
  );
}
